import { useCallback, useMemo } from 'react';
import { useAuthSessionStore } from '../../auth/session-store';
import { useAnnotationStore } from '../store/local-annotations';
import { useCloudAnnotationsStore } from '../store/cloud-annotations';
import type { AddAnnotationInput, Annotation } from '../types/annotation';
import type { CloudAnnotation } from '../types/cloud';

export interface UnifiedAnnotation {
  id: string;
  conversationId: string;
  conversationName: string;
  messageId: string;
  messageText: string;
  platform: 'claude' | 'chatgpt' | null;
  note: string;
  createdAt: number;
  updatedAt: number;
}

export interface UseAnnotationSourceResult {
  isCloudSource: boolean;
  isLoading: boolean;
  error: string | null;
  annotations: UnifiedAnnotation[];
  loadAnnotations: () => Promise<void>;
  addAnnotation: (input: AddAnnotationInput) => Promise<void>;
  removeAnnotation: (id: string) => Promise<void>;
  removeAnnotationByMessageId: (messageId: string) => Promise<void>;
  updateNote: (id: string, note: string) => Promise<void>;
  getAnnotationByMessageId: (messageId: string) => UnifiedAnnotation | undefined;
  clearError: () => void;
}

function cloudToUnifiedAnnotation(annotation: CloudAnnotation): UnifiedAnnotation {
  return {
    id: annotation.id,
    conversationId: annotation.conversationId,
    conversationName: annotation.conversationName,
    messageId: annotation.messageId,
    messageText: annotation.messageText,
    platform: annotation.platform ?? null,
    note: annotation.note,
    createdAt: new Date(annotation.createdAt).getTime(),
    updatedAt: new Date(annotation.updatedAt).getTime(),
  };
}

function localToUnifiedAnnotation(annotation: Annotation): UnifiedAnnotation {
  return {
    id: annotation.id,
    conversationId: annotation.conversationId,
    conversationName: annotation.conversationName,
    messageId: annotation.messageId,
    messageText: annotation.messageText,
    platform: annotation.platform ?? null,
    note: annotation.note,
    createdAt: annotation.createdAt,
    updatedAt: annotation.updatedAt,
  };
}

export function useAnnotationSource(): UseAnnotationSourceResult {
  const isAuthenticated = useAuthSessionStore((state) => state.isAuthenticated);
  const isPro = useAuthSessionStore((state) => state.subscription?.isPro ?? false);
  const isCloudSource = isPro && isAuthenticated;

  const localAnnotations = useAnnotationStore((state) => state.annotations);
  const localIsLoading = useAnnotationStore((state) => state.isLoading);
  const localError = useAnnotationStore((state) => state.error);
  const localLoadAnnotations = useAnnotationStore((state) => state.loadAnnotations);
  const localAddAnnotation = useAnnotationStore((state) => state.addAnnotation);
  const localRemoveAnnotation = useAnnotationStore((state) => state.removeAnnotation);
  const localRemoveAnnotationByMessageId = useAnnotationStore(
    (state) => state.removeAnnotationByMessageId,
  );
  const localUpdateNote = useAnnotationStore((state) => state.updateNote);

  const cloudAnnotations = useCloudAnnotationsStore((state) => state.annotations);
  const cloudIsLoading = useCloudAnnotationsStore((state) => state.isLoading);
  const cloudError = useCloudAnnotationsStore((state) => state.error);
  const cloudFetchAnnotations = useCloudAnnotationsStore((state) => state.fetchAnnotations);
  const cloudCreateAnnotation = useCloudAnnotationsStore((state) => state.createAnnotation);
  const cloudDeleteAnnotation = useCloudAnnotationsStore((state) => state.deleteAnnotation);
  const cloudUpdateNote = useCloudAnnotationsStore((state) => state.updateNote);
  const cloudClearError = useCloudAnnotationsStore((state) => state.clearError);

  const annotations = useMemo(() => {
    if (isCloudSource) {
      return cloudAnnotations.map(cloudToUnifiedAnnotation);
    }

    return localAnnotations.map(localToUnifiedAnnotation);
  }, [isCloudSource, cloudAnnotations, localAnnotations]);

  const isLoading = isCloudSource ? cloudIsLoading : localIsLoading;
  const error = isCloudSource ? cloudError : localError;

  const loadAnnotations = useCallback(async () => {
    if (isCloudSource) {
      await cloudFetchAnnotations();
      return;
    }

    await localLoadAnnotations();
  }, [isCloudSource, cloudFetchAnnotations, localLoadAnnotations]);

  const addAnnotation = useCallback(async (input: AddAnnotationInput) => {
    if (isCloudSource) {
      await cloudCreateAnnotation(input);
      return;
    }

    await localAddAnnotation(input);
  }, [isCloudSource, cloudCreateAnnotation, localAddAnnotation]);

  const removeAnnotation = useCallback(async (id: string) => {
    if (isCloudSource) {
      await cloudDeleteAnnotation(id);
      return;
    }

    await localRemoveAnnotation(id);
  }, [isCloudSource, cloudDeleteAnnotation, localRemoveAnnotation]);

  const removeAnnotationByMessageId = useCallback(async (messageId: string) => {
    if (isCloudSource) {
      const matchingAnnotations = annotations.filter(
        (annotation) => annotation.messageId === messageId,
      );
      await Promise.all(
        matchingAnnotations.map((annotation) => cloudDeleteAnnotation(annotation.id)),
      );
      return;
    }

    await localRemoveAnnotationByMessageId(messageId);
  }, [isCloudSource, annotations, cloudDeleteAnnotation, localRemoveAnnotationByMessageId]);

  const updateNote = useCallback(async (id: string, note: string) => {
    if (isCloudSource) {
      await cloudUpdateNote(id, note);
      return;
    }

    await localUpdateNote(id, note);
  }, [isCloudSource, cloudUpdateNote, localUpdateNote]);

  const getAnnotationByMessageId = useCallback((messageId: string) => {
    return annotations.find((annotation) => annotation.messageId === messageId);
  }, [annotations]);

  const clearError = useCallback(() => {
    if (isCloudSource) {
      cloudClearError();
    }
  }, [isCloudSource, cloudClearError]);

  return {
    isCloudSource,
    isLoading,
    error,
    annotations,
    loadAnnotations,
    addAnnotation,
    removeAnnotation,
    removeAnnotationByMessageId,
    updateNote,
    getAnnotationByMessageId,
    clearError,
  };
}
